/**
 * ProductLightbox — Phase 19-D
 *
 * Premium fullscreen lightbox voor productafbeeldingen op /webshop.
 * Open vanuit de hoofdafbeelding op een productkaart.
 *
 * Features:
 *   - Embla-carousel voor soepele swipe/snap navigation (mobile + desktop)
 *   - Framer Motion voor entry/exit en thumb-highlight transitions
 *   - Keyboard arrows (← → ESC) op desktop
 *   - Tap-to-zoom toggle (CSS scale) op desktop; mobile gebruikt native
 *     `touch-action: pinch-zoom` zodat het OS de pinch zelf afhandelt
 *   - Image preloading op active index +1 / index -1
 *   - Focus trap binnen modal voor accessibility
 *   - ARIA labels, screenreader vriendelijk
 *   - Backdrop blur, klik-buiten sluit
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   images: Array<{url, alt, title}>
 *   initialIndex: number
 *   productName: string  (for aria-label)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fullUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

export default function ProductLightbox({
  open,
  onClose,
  images = [],
  initialIndex = 0,
  productName = "",
}) {
  const safeImages = useMemo(
    () => (Array.isArray(images) ? images.filter((i) => i?.url) : []),
    [images]
  );
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "center",
    startIndex: initialIndex,
    skipSnaps: false,
  });
  const [activeIdx, setActiveIdx] = useState(initialIndex);
  const [zoomed, setZoomed] = useState(false);
  const closeBtnRef = useRef(null);
  const modalRef = useRef(null);

  // Embla → React state sync
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setActiveIdx(emblaApi.selectedScrollSnap());
      setZoomed(false);
    };
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  // Re-sync when caller opens with a different initialIndex
  useEffect(() => {
    if (open && emblaApi) {
      emblaApi.scrollTo(initialIndex, true);
      setZoomed(false);
    }
  }, [open, initialIndex, emblaApi]);

  // Preload neighbour images to avoid flicker
  useEffect(() => {
    if (!open) return;
    [activeIdx - 1, activeIdx + 1].forEach((i) => {
      if (i >= 0 && i < safeImages.length) {
        const img = new Image();
        img.src = fullUrl(safeImages[i].url);
      }
    });
  }, [open, activeIdx, safeImages]);

  // Keyboard navigation + ESC + focus management
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      } else if (e.key === "ArrowRight") {
        emblaApi?.scrollNext();
      } else if (e.key === "ArrowLeft") {
        emblaApi?.scrollPrev();
      } else if (e.key === "Tab") {
        // Simple focus trap: keep focus on close button
        const focusables = modalRef.current?.querySelectorAll(
          'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    // Set initial focus to close button (accessibility)
    setTimeout(() => closeBtnRef.current?.focus(), 50);
    // Body scroll-lock
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, emblaApi, onClose]);

  const onBackdropClick = useCallback((e) => {
    // Sluit alleen als klik niet op een interactief element of img landt.
    // Dit laat de hele zwarte/lege ruimte als "klik-buiten" werken,
    // zonder dat we per child stopPropagation hoeven te zetten (die
    // brak eerder de e.target === e.currentTarget check).
    const t = e.target;
    if (
      t.closest?.("button") ||
      t.closest?.("img") ||
      t.closest?.("a") ||
      t.closest?.("input") ||
      t.closest?.("[data-no-backdrop-close]")
    ) {
      return;
    }
    onClose?.();
  }, [onClose]);

  const onPrev = () => emblaApi?.scrollPrev();
  const onNext = () => emblaApi?.scrollNext();

  const goTo = (i) => emblaApi?.scrollTo(i);

  if (typeof document === "undefined") return null;
  if (safeImages.length === 0) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-label={`Afbeeldingen voor ${productName || "product"}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex flex-col"
          onClick={onBackdropClick}
          data-testid="product-lightbox"
        >
          {/* Top bar */}
          <div
            className="flex items-center justify-between px-4 sm:px-6 py-3 text-white"
            data-no-backdrop-close
          >
            <span className="text-xs sm:text-sm uppercase tracking-wider font-bold opacity-80">
              {productName}
              <span className="ml-2 opacity-60">
                {activeIdx + 1} / {safeImages.length}
              </span>
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setZoomed((z) => !z)}
                className="hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white"
                aria-label={zoomed ? "Inzoomen uitschakelen" : "Inzoomen"}
                data-testid="lightbox-zoom-toggle"
              >
                {zoomed ? <ZoomOut className="w-4 h-4" /> : <ZoomIn className="w-4 h-4" />}
              </button>
              <button
                ref={closeBtnRef}
                type="button"
                onClick={onClose}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white"
                aria-label="Sluit afbeeldingen"
                data-testid="lightbox-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Main slider — embla provides swipe + arrow click */}
          <div
            className="flex-1 relative overflow-hidden"
          >
            <div
              ref={emblaRef}
              className="overflow-hidden h-full"
              data-testid="lightbox-carousel"
            >
              <div className="flex h-full">
                {safeImages.map((img, i) => (
                  <div
                    key={`${img.url}-${i}`}
                    className="flex-[0_0_100%] min-w-0 h-full flex items-center justify-center px-4 sm:px-12 select-none"
                    style={{ touchAction: "pinch-zoom" }}
                    data-testid={`lightbox-slide-${i}`}
                  >
                    <motion.img
                      src={fullUrl(img.url)}
                      alt={img.alt || `${productName} ${i + 1}`}
                      title={img.title || img.alt || ""}
                      draggable={false}
                      loading={i === initialIndex ? "eager" : "lazy"}
                      decoding="async"
                      className="max-w-full max-h-full object-contain cursor-zoom-in pointer-events-auto"
                      style={{
                        cursor: i === activeIdx
                          ? (zoomed ? "zoom-out" : "zoom-in")
                          : "default",
                        transform:
                          i === activeIdx && zoomed ? "scale(1.6)" : "scale(1)",
                        transition: "transform 0.25s ease",
                        willChange: "transform",
                      }}
                      onClick={() => {
                        if (i === activeIdx) setZoomed((z) => !z);
                      }}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Prev/Next desktop chevrons */}
            {safeImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={onPrev}
                  disabled={activeIdx === 0}
                  className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Vorige afbeelding"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  onClick={onNext}
                  disabled={activeIdx === safeImages.length - 1}
                  className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Volgende afbeelding"
                  data-testid="lightbox-next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnail strip */}
          {safeImages.length > 1 && (
            <div
              className="px-3 sm:px-6 pb-4 pt-2 overflow-x-auto"
              data-no-backdrop-close
              data-testid="lightbox-thumbs"
            >
              <div className="flex gap-2 justify-center min-w-min">
                {safeImages.map((img, i) => (
                  <button
                    key={`thumb-${img.url}-${i}`}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-label={`Toon afbeelding ${i + 1}`}
                    aria-current={i === activeIdx}
                    className={`flex-shrink-0 w-14 h-14 border-2 ${
                      i === activeIdx
                        ? "border-white"
                        : "border-white/20 hover:border-white/60"
                    } bg-white/5 overflow-hidden transition-colors`}
                    data-testid={`lightbox-thumb-${i}`}
                  >
                    <img
                      src={fullUrl(img.url)}
                      alt=""
                      className="w-full h-full object-contain"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
